import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Receipt, TrendingUp, Wallet, PlusCircle, ArrowUpRight } from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import api, { fmtTRY, fmtDate } from "@/lib/api";
import { useProject } from "@/context/ProjectContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

const monthLabel = (m) => {
  if (!m) return "";
  const [y, mo] = m.split("-");
  const names = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];
  return `${names[parseInt(mo, 10) - 1]} ${y.slice(2)}`;
};

function KpiCard({ icon: Icon, label, value, testid }) {
  return (
    <div className="rounded-md border border-border bg-card p-6" data-testid={testid}>
      <div className="flex items-center justify-between">
        <p className="overline">{label}</p>
        <Icon className="h-4 w-4 text-slate-400" />
      </div>
      <p className="mt-3 font-heading text-3xl font-bold tabular-nums">{value}</p>
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  const { project } = useProject();

  useEffect(() => {
    api.get("/dashboard").then((res) => setData(res.data));
  }, []);

  if (!data)
    return (
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {[1, 2, 3].map((i) => <Skeleton key={i} className="h-32 rounded-md" />)}
      </div>
    );

  const chartData = data.monthly.map((m) => ({ ...m, label: monthLabel(m.month) }));

  return (
    <div className="animate-fade-up">
      <div className="relative mb-6 overflow-hidden rounded-md border border-border" data-testid="dashboard-banner">
        <img src={project.imageUrl} alt={`${project.name} - Horoz Group`} className="h-40 w-full object-cover sm:h-48" />
        <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary/70 to-primary/10" />
        <div className="absolute inset-0 flex flex-col justify-center p-6 text-primary-foreground sm:p-8">
          <p className="overline text-white/60">Şantiye Projesi · Horoz Group</p>
          <h2 className="font-heading text-4xl font-bold leading-none tracking-tight sm:text-5xl">{project.name}</h2>
        </div>
      </div>

      <PageHeader overline="Genel Bakış" title="Gösterge Paneli" subtitle="Şantiye giderlerinin özeti">
        <Button onClick={() => navigate("/expenses/new")} data-testid="dashboard-new-expense-button">
          <PlusCircle className="mr-2 h-4 w-4" /> Yeni Gider
        </Button>
      </PageHeader>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <KpiCard icon={Wallet} label="Toplam Gider" value={fmtTRY(data.total)} testid="kpi-total" />
        <KpiCard icon={TrendingUp} label="Bu Ay" value={fmtTRY(data.monthly_total)} testid="kpi-monthly" />
        <KpiCard icon={Receipt} label="Gider Sayısı" value={data.count} testid="kpi-count" />
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-5">
        {/* Chart */}
        <div className="rounded-md border border-border bg-card p-6 lg:col-span-3" data-testid="dashboard-chart">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-heading text-base font-bold">Aylık Gider Gelişimi</h2>
          </div>
          {chartData.length === 0 ? (
            <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">
              Henüz veri yok
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="fill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(222 47% 11%)" stopOpacity={0.15} />
                    <stop offset="100%" stopColor="hsl(222 47% 11%)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(214 32% 91%)" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false}
                  tickFormatter={(v) => (v >= 1000 ? `${v / 1000}b` : v)} />
                <Tooltip
                  formatter={(v) => [fmtTRY(v), "Gider"]}
                  contentStyle={{ borderRadius: 6, border: "1px solid #e2e8f0", fontSize: 13 }}
                />
                <Area type="monotone" dataKey="total" stroke="hsl(222 47% 11%)" strokeWidth={2} fill="url(#fill)" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Latest */}
        <div className="rounded-md border border-border bg-card p-6 lg:col-span-2" data-testid="dashboard-latest">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-heading text-base font-bold">Son Giderler</h2>
            <button onClick={() => navigate("/expenses")} className="flex items-center gap-1 text-xs text-slate-500 transition-colors hover:text-primary">
              Tümü <ArrowUpRight className="h-3 w-3" />
            </button>
          </div>
          {data.latest.length === 0 ? (
            <div className="flex h-48 items-center justify-center text-sm text-muted-foreground">Henüz gider yok</div>
          ) : (
            <div className="divide-y divide-border">
              {data.latest.map((e) => (
                <div key={e.id} className="flex items-center justify-between py-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{e.description}</p>
                    <p className="text-xs text-muted-foreground">{e.trade_name} · {fmtDate(e.expense_date)}</p>
                  </div>
                  <span className="ml-3 shrink-0 text-sm font-semibold tabular-nums">{fmtTRY(e.amount)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
